import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import type { NewsTag } from '@/types/newsService';
import { cn } from '@/lib/cn';

interface Props {
  categories: NewsTag[];
  activeSlug: string | null; // null = "For You"
  onSelect: (slug: string | null) => void;
}

interface TabItem {
  slug: string | null;
  name: string;
  color?: string;
}

/**
 * Horizontal pill row for top-level category tags + a "For You" pseudo-tab.
 * Ported from mobile-app/src/components/news/CategoryTabs.tsx — RN
 * horizontal FlatList becomes a `flex` div with `overflow-x-auto`.
 */
export const CategoryTabs = memo(function CategoryTabs({
  categories,
  activeSlug,
  onSelect,
}: Props) {
  const { t } = useTranslation('news');
  const tabs: TabItem[] = [
    { slug: null, name: t('forYou', { defaultValue: 'For You' }) },
    ...categories.map((c) => ({ slug: c.slug, name: c.name, color: c.color })),
  ];

  return (
    <div className="no-scrollbar overflow-x-auto py-2">
      <div className="flex gap-2 px-4">
        {tabs.map((tab) => {
          const isActive = tab.slug === activeSlug;
          return (
            <button
              key={tab.slug ?? 'for-you'}
              type="button"
              onClick={() => onSelect(tab.slug)}
              className={cn(
                'whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-opacity active:opacity-80',
                isActive
                  ? 'bg-brand text-brand-fg'
                  : 'bg-bg-secondary text-fg-muted',
              )}
            >
              {tab.name}
            </button>
          );
        })}
      </div>
    </div>
  );
});
